import { createApi } from '@reduxjs/toolkit/query/react';

import { apiCompanyAccessTokens } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { CompanyAccessKey } from '~main/models';

export const typePrefix = 'companyAccessKeys';

interface BasicResponse {
  success: boolean;
  message: string;
}

interface CreateResponse extends BasicResponse {
  data: {
    id: string;
    access_key: string;
    access_token: string;
  };
}

interface CreateRequest {
  name: string;
  expires_at: string;
  tags: { id: string }[];
}

export const companyAccessKeysApi = createApi({
  reducerPath: 'companyAccessKeysApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiCompanyAccessTokens }),
  tagTypes: ['companyAccessKeys', 'companyAccessKey'],
  endpoints: (builder) => ({
    getCompanyAccessKeys: builder.query<CompanyAccessKey[], Request | void>({
      query: () => ({ url: '', method: 'GET' }),
      providesTags: [{ type: 'companyAccessKeys', id: 'LIST' }],
      transformResponse: (payload) => unwrap<CompanyAccessKey[]>(payload),
    }),
    getCompanyAccessKey: builder.query<CompanyAccessKey, { id: string }>({
      query: ({ id, ...restParams }) => ({
        url: `/${id}`,
        method: 'GET',
        ...restParams,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'companyAccessKey', id }],
      transformResponse: (payload) => unwrap<CompanyAccessKey>(payload),
    }),
    createCompanyAccessKey: builder.mutation<CreateResponse, CreateRequest>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'companyAccessKeys', id: 'LIST' }],
    }),
    revokeCompanyAccessKey: builder.mutation<BasicResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'companyAccessKeys', id: 'LIST' }],
      transformResponse: (payload) => unwrap<BasicResponse>(payload),
    }),
  }),
});

export const {
  useGetCompanyAccessKeysQuery,
  useGetCompanyAccessKeyQuery,
  useCreateCompanyAccessKeyMutation,
  useRevokeCompanyAccessKeyMutation,
} = companyAccessKeysApi;
